
import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import EvaVideo from "./EvaVideo";
import EvaAssessmentReport from "./EvaAssessmentReport";
import "./EvaQuestionnaire.css";


//========================================================
// EVA Interactive Questionnaire
//========================================================

const PCM_BYTES_PER_SECOND = 16000 * 2;
const SPEECH_PAUSE_MS = 650;


//========================================================
// Question Definitions
//
// These can eventually come from UAR as JSON.
// Coordinates and answers are demonstration data.
//========================================================

const QUESTIONS = [

    {
        id: "condition",
        type: "single",
        title: "Room Condition",
        question: "How would you rate the overall condition of this room?",
        narration:
            "Let's begin with a simple condition assessment. " +
            "How would you rate the overall condition of this room?",
        options: [
            "Excellent",
            "Good",
            "Fair",
            "Poor",
            "Critical"
        ],
        correct: "Good",
        explanation:
            "For this demonstration, Good is our reference assessment. " +
            "In a real survey, the rating would depend on observed evidence."
    },

    {
        id: "firehazards",
        type: "multiple",
        title: "Fire Hazard Identification",
        question: "Which of these situations could present a fire safety concern?",
        narration:
            "Now select all the situations that could create " +
            "a fire safety concern.",
        options: [
            "Blocked emergency exits",
            "Damaged electrical cables",
            "Unobstructed escape routes",
            "Wedged-open fire doors",
            "Clearly labelled fire extinguishers"
        ],
        correct: [
            "Blocked emergency exits",
            "Damaged electrical cables",
            "Wedged-open fire doors"
        ],
        explanation:
            "Blocked exits, damaged electrical cables and improperly " +
            "wedged-open fire doors can all present safety concerns."
    },

    {
        id: "description",
        type: "text",
        title: "Defect Description",
        question: "Describe a possible workplace safety defect.",
        narration:
            "Next, enter a short description of a workplace " +
            "safety defect. You can use an example from the briefing.",
        placeholder:
            "For example: A trailing cable crosses the main walkway.",
        explanation:
            "Your description has been captured. In a live survey " +
            "this would become part of the inspection record."
    },

    {
        id: "grid",
        type: "grid",
        title: "Spot the Trip Hazards",
        question: "Select every square containing a potential hazard.",
        narration:
            "Here's our interactive image challenge. " +
            "Look at the photograph and select the squares " +
            "containing the potential hazard.",
        image: "/eva/images/hs/slips-trips.jpg",
        gridSize: 4,
        correct: [5, 6, 9, 10],
        explanation:
            "The highlighted squares are our predefined " +
            "demonstration hazard area. In the finished system, " +
            "these could be generated from AI image analysis."
    },

    {
        id: "hotspot",
        type: "hotspot",
        title: "Locate the Obstruction",
        question: "Click the photograph to mark the obstruction.",
        narration:
            "Now let's try something different. " +
            "Click directly on the photograph to identify " +
            "the obstructed emergency exit.",
        image: "/eva/images/hs/emergency-exit.jpg",
        target: {
            x: 50,
            y: 55,
            radius: 28
        },
        explanation:
            "The marker records the location as percentage " +
            "coordinates, so it remains accurate when the image resizes."
    },

    {
        id: "firedoor",
        type: "yesno",
        title: "Fire Door Inspection",
        question: "Should an ordinary fire door be wedged open?",
        narration:
            "A quick fire safety question. " +
            "Should an ordinary fire door be wedged open?",
        options: [
            "Yes",
            "No",
            "Not Applicable"
        ],
        correct: "No",
        explanation:
            "Fire doors should not be wedged open. " +
            "Approved automatic hold-open systems are a separate matter."
    },

    {
        id: "severity",
        type: "slider",
        title: "Risk Severity",
        question: "Rate the severity of an obstructed emergency exit.",
        narration:
            "Use the slider to rate the potential severity " +
            "of an obstructed emergency exit.",
        min: 1,
        max: 5,
        correct: 5,
        labels: [
            "Negligible",
            "Minor",
            "Moderate",
            "Major",
            "Critical"
        ],
        explanation:
            "For our training example, we've classified this " +
            "scenario as Critical. Actual risk assessment must " +
            "consider the circumstances and the assessment method."
    },

    {
        id: "floorplan",
        type: "floorplan",
        title: "Locate the Affected Room",
        question: "Select the room where the defect was reported.",
        narration:
            "Now imagine we've received a defect report " +
            "for the plant room. Select the plant room " +
            "on this simplified floor plan.",
        rooms: [
            { id: "office", label: "Office", x: 5, y: 8, w: 43, h: 38 },
            { id: "meeting", label: "Meeting", x: 52, y: 8, w: 43, h: 38 },
            { id: "plant", label: "Plant Room", x: 5, y: 54, w: 43, h: 38 },
            { id: "store", label: "Store", x: 52, y: 54, w: 43, h: 38 }
        ],
        correct: "plant",
        explanation:
            "The selected room can eventually be linked to " +
            "an OmniScan location, floor plan and asset register."
    },

    {
        id: "evidence",
        type: "evidence",
        title: "Photographic Evidence",
        question: "Attach a photograph of an observed defect.",
        narration:
            "For our next question, attach a photograph " +
            "as supporting evidence. On a mobile device, " +
            "this could be captured directly using the camera.",
        explanation:
            "The photograph is attached locally for this demonstration. " +
            "A production survey would upload it securely to the server."
    },

    {
        id: "sequence",
        type: "sequence",
        title: "Emergency Response",
        question: "Arrange these actions into the correct order.",
        narration:
            "Finally, arrange these emergency response actions " +
            "into the correct order for this example scenario.",
        options: [
            { id: "evacuate", label: "Evacuate using a safe route" },
            { id: "alarm", label: "Raise the alarm" },
            { id: "assemble", label: "Report to the assembly point" }
        ],
        correct: [
            "alarm",
            "evacuate",
            "assemble"
        ],
        explanation:
            "For this example, raise the alarm, evacuate safely " +
            "and report to the designated assembly point. " +
            "Always follow the site's specific emergency procedures."
    }

];


//========================================================
// Helpers
//========================================================

function arraysEqual(a, b) {

    if (!Array.isArray(a) || !Array.isArray(b)) {
        return false;
    }

    return (
        a.length === b.length &&
        a.every((value, index) => value === b[index])
    );

}


function answersMatch(question, answer) {

    if (question.correct === undefined) {
        return null;
    }

    if (question.type === "multiple" ||
        question.type === "grid") {

        const actual = [...(answer || [])].sort();
        const expected = [...question.correct].sort();

        return arraysEqual(actual, expected);
    }

    if (question.type === "hotspot") {

        if (!answer) {
            return false;
        }

        const dx = answer.x - question.target.x;
        const dy = answer.y - question.target.y;

        return Math.sqrt(dx * dx + dy * dy) <=
            question.target.radius;
    }

    if (question.type === "sequence") {

        return arraysEqual(answer, question.correct);
    }

    return answer === question.correct;

}


//========================================================
// Photograph with Error Fallback
//========================================================

function QuestionImage({ src, alt, children }) {

    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setFailed(false);
    }, [src]);

    return (

        <div className="eva-q-image-frame">

            {!failed && (

                <img
                    src={src}
                    alt={alt}
                    onError={() => setFailed(true)}
                    draggable={false}
                />

            )}

            {failed && (

                <div className="eva-q-image-missing">

                    <strong>Example photograph unavailable</strong>

                    <span>{src}</span>

                </div>

            )}

            {children}

        </div>

    );

}


//========================================================
// Question Input Renderer
//========================================================

function QuestionInput({
    question,
    answer,
    onChange,
    locked
}) {

    //----------------------------------------------------
    // Single Choice / Yes-No
    //----------------------------------------------------

    if (question.type === "single" ||
        question.type === "yesno") {

        return (

            <div className="eva-q-options">

                {question.options.map(option => (

                    <button
                        key={option}
                        type="button"
                        disabled={locked}
                        className={
                            "eva-q-option " +
                            (answer === option ? "selected" : "")
                        }
                        onClick={() => onChange(option)}
                    >

                        <span className="eva-q-radio" />

                        {option}

                    </button>

                ))}

            </div>

        );

    }

    //----------------------------------------------------
    // Multiple Choice
    //----------------------------------------------------

    if (question.type === "multiple") {

        const selected = answer || [];

        return (

            <div className="eva-q-options">

                {question.options.map(option => (

                    <button
                        key={option}
                        type="button"
                        disabled={locked}
                        className={
                            "eva-q-option " +
                            (selected.includes(option) ? "selected" : "")
                        }
                        onClick={() => {

                            if (selected.includes(option)) {

                                onChange(
                                    selected.filter(item => item !== option)
                                );

                            } else {

                                onChange([...selected, option]);

                            }

                        }}
                    >

                        <span className="eva-q-checkbox">
                            {selected.includes(option) ? "✓" : ""}
                        </span>

                        {option}

                    </button>

                ))}

            </div>

        );

    }

    //----------------------------------------------------
    // Free Text
    //----------------------------------------------------

    if (question.type === "text") {

        return (

            <textarea
                className="eva-q-textarea"
                rows={6}
                disabled={locked}
                value={answer || ""}
                placeholder={question.placeholder}
                onChange={event => onChange(event.target.value)}
            />

        );

    }

    //----------------------------------------------------
    // Image Grid - CAPTCHA Style
    //----------------------------------------------------

    if (question.type === "grid") {

        const selected = answer || [];
        const size = question.gridSize || 4;

        return (

            <div className="eva-q-image-interaction">

                <QuestionImage
                    src={question.image}
                    alt={question.title}
                >

                    <div
                        className="eva-q-grid"
                        style={{
                            gridTemplateColumns:
                                `repeat(${size}, 1fr)`,
                            gridTemplateRows:
                                `repeat(${size}, 1fr)`
                        }}
                    >

                        {Array.from(
                            { length: size * size },
                            (_, index) => index
                        ).map(index => (

                            <button
                                key={index}
                                type="button"
                                disabled={locked}
                                aria-label={
                                    `Select image square ${index + 1}`
                                }
                                aria-pressed={selected.includes(index)}
                                className={
                                    "eva-q-grid-cell " +
                                    (selected.includes(index)
                                        ? "selected"
                                        : "")
                                }
                                onClick={() => {

                                    if (selected.includes(index)) {

                                        onChange(
                                            selected.filter(
                                                value => value !== index
                                            )
                                        );

                                    } else {

                                        onChange([...selected, index]);

                                    }

                                }}
                            >

                                {selected.includes(index) && "✓"}

                            </button>

                        ))}

                    </div>

                </QuestionImage>

                <small>
                    {selected.length} square(s) selected.
                    Click a square again to deselect it.
                </small>

            </div>

        );

    }

    //----------------------------------------------------
    // Image Hotspot
    //----------------------------------------------------

    if (question.type === "hotspot") {

        return (

            <div className="eva-q-image-interaction">

                <QuestionImage
                    src={question.image}
                    alt={question.title}
                >

                    <div
                        className={
                            "eva-q-hotspot-layer " +
                            (locked ? "locked" : "")
                        }
                        onClick={event => {

                            if (locked) {
                                return;
                            }

                            const bounds =
                                event.currentTarget.getBoundingClientRect();

                            const x =
                                (event.clientX - bounds.left) /
                                bounds.width * 100;

                            const y =
                                (event.clientY - bounds.top) /
                                bounds.height * 100;

                            onChange({
                                x: Math.max(0, Math.min(100, x)),
                                y: Math.max(0, Math.min(100, y))
                            });

                        }}
                    >

                        {answer && (

                            <span
                                className="eva-q-hotspot-marker"
                                style={{
                                    left: `${answer.x}%`,
                                    top: `${answer.y}%`
                                }}
                            >
                                +
                            </span>

                        )}

                    </div>

                </QuestionImage>

                <small>
                    Click anywhere on the photograph to place your marker.
                </small>

            </div>

        );

    }

    //----------------------------------------------------
    // Severity Slider
    //----------------------------------------------------

    if (question.type === "slider") {

        const value = answer ?? 3;

        return (

            <div className="eva-q-slider-panel">

                <div className="eva-q-slider-value">

                    <strong>{value}</strong>

                    <span>
                        {question.labels[value - question.min]}
                    </span>

                </div>

                <input
                    type="range"
                    min={question.min}
                    max={question.max}
                    value={value}
                    disabled={locked}
                    onChange={event =>
                        onChange(Number(event.target.value))
                    }
                />

                <div className="eva-q-slider-ends">
                    <span>Negligible</span>
                    <span>Critical</span>
                </div>

            </div>

        );

    }

    //----------------------------------------------------
    // Floor Plan Room Selection
    //----------------------------------------------------

    if (question.type === "floorplan") {

        return (

            <div className="eva-q-floorplan">

                {question.rooms.map(room => (

                    <button
                        key={room.id}
                        type="button"
                        disabled={locked}
                        className={
                            "eva-q-room " +
                            (answer === room.id ? "selected" : "")
                        }
                        style={{
                            left: `${room.x}%`,
                            top: `${room.y}%`,
                            width: `${room.w}%`,
                            height: `${room.h}%`
                        }}
                        onClick={() => onChange(room.id)}
                    >

                        {room.label}

                    </button>

                ))}

            </div>

        );

    }

    //----------------------------------------------------
    // Photo Evidence
    //----------------------------------------------------

    if (question.type === "evidence") {

        return (

            <div className="eva-q-evidence-upload">

                <label className="eva-q-upload-label">

                    <span>📷</span>

                    <strong>
                        {answer
                            ? "Replace Photograph"
                            : "Select / Capture Photograph"}
                    </strong>

                    <small>
                        JPG, PNG or another supported image format
                    </small>

                    <input
                        type="file"
                        accept="image/*"
                        disabled={locked}
                        onChange={event => {

                            const file = event.target.files?.[0];

                            if (file) {
                                onChange(file);
                            }

                        }}
                    />

                </label>

                {answer && (

                    <div className="eva-q-file-selected">

                        ✓ {answer.name}

                    </div>

                )}

            </div>

        );

    }

    //----------------------------------------------------
    // Sequence Ordering
    //----------------------------------------------------

    if (question.type === "sequence") {

        const items = answer || question.options.map(
            option => option.id
        );

        return (

            <div className="eva-q-sequence">

                {items.map((id, index) => {

                    const item = question.options.find(
                        option => option.id === id
                    );

                    return (

                        <div
                            key={id}
                            className="eva-q-sequence-row"
                        >

                            <span className="eva-q-sequence-number">
                                {index + 1}
                            </span>

                            <strong>{item?.label}</strong>

                            <div className="eva-q-sequence-controls">

                                <button
                                    type="button"
                                    disabled={locked || index === 0}
                                    onClick={() => {

                                        const next = [...items];

                                        [
                                            next[index - 1],
                                            next[index]
                                        ] = [
                                            next[index],
                                            next[index - 1]
                                        ];

                                        onChange(next);

                                    }}
                                >
                                    ↑
                                </button>

                                <button
                                    type="button"
                                    disabled={
                                        locked ||
                                        index === items.length - 1
                                    }
                                    onClick={() => {

                                        const next = [...items];

                                        [
                                            next[index],
                                            next[index + 1]
                                        ] = [
                                            next[index + 1],
                                            next[index]
                                        ];

                                        onChange(next);

                                    }}
                                >
                                    ↓
                                </button>

                            </div>

                        </div>

                    );

                })}

            </div>

        );

    }

    return null;

}


//========================================================
// Main Component
//========================================================

export default function EvaQuestionnaire({ onClose }) {

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [phase, setPhase] = useState("intro");
    const [ready, setReady] = useState(false);
    const [questionIndex, setQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [status, setStatus] = useState("Preparing Fiona");
    const [speaking, setSpeaking] = useState(false);
	const [showReport, setShowReport] = useState(false);

    //----------------------------------------------------
    // Refs
    //----------------------------------------------------

    const videoRef = useRef(null);
    const timerRef = useRef(null);
    const runIdRef = useRef(0);
    const readyRef = useRef(false);

    //----------------------------------------------------
    // Current Question
    //----------------------------------------------------

    const question = QUESTIONS[questionIndex];

    const answer = answers[question.id];

    const result = submitted
        ? answersMatch(question, answer)
        : null;

    const progress = Math.round(
        questionIndex / QUESTIONS.length * 100
    );

    //----------------------------------------------------
    // Timer Management
    //----------------------------------------------------

    const clearTimer = useCallback(() => {

        if (timerRef.current) {

            clearTimeout(timerRef.current);
            timerRef.current = null;

        }

    }, []);

    //----------------------------------------------------
    // Cleanup
    //----------------------------------------------------

    useEffect(() => {

        return () => {

            runIdRef.current += 1;
            clearTimer();

        };

    }, [clearTimer]);

    //----------------------------------------------------
    // Fiona Ready
    //----------------------------------------------------

    const handleReady = useCallback(() => {

        readyRef.current = true;
        setReady(true);
        setStatus("Fiona is ready");

    }, []);

    //----------------------------------------------------
    // Speak
    //----------------------------------------------------

    const speak = useCallback(async text => {

        if (!readyRef.current ||
            !videoRef.current?.speak) {

            return;
        }

        const runId = ++runIdRef.current;

        clearTimer();
        setSpeaking(true);
        setStatus("Fiona is speaking");

        try {

            const bytes = await videoRef.current.speak(text);

            if (runId !== runIdRef.current) {
                return;
            }

            const words = text.trim().split(/\s+/).length;

            const duration =
                typeof bytes === "number"
                    ? bytes / PCM_BYTES_PER_SECOND * 1000
                    : words / 120 * 60000;

            timerRef.current = setTimeout(() => {

                if (runId !== runIdRef.current) {
                    return;
                }

                setSpeaking(false);
                setStatus("Awaiting response");

            }, Math.max(1500, duration) + SPEECH_PAUSE_MS);

        }
        catch (error) {

            console.error(
                "Questionnaire speech failed:",
                error
            );

            if (runId !== runIdRef.current) {
                return;
            }

            setSpeaking(false);
            setStatus("Awaiting response");

        }

    }, [clearTimer]);

    //----------------------------------------------------
    // Start
    //----------------------------------------------------

    const startQuestionnaire = () => {

        if (!readyRef.current) {
            return;
        }

        setQuestionIndex(0);
		setAnswers({
		    severity: 3,
		    sequence: QUESTIONS.find(
		        item => item.id === "sequence"
		    ).options.map(option => option.id)
		});

		setShowReport(false);
        setSubmitted(false);
        setPhase("question");
        setStatus("Question 1 of 10");

        speak(
            "Welcome to your interactive workplace assessment. " +
            "I'll guide you through ten different question types. " +
            QUESTIONS[0].narration
        );

    };

    //----------------------------------------------------
    // Change Answer
    //----------------------------------------------------

    const updateAnswer = value => {

        if (submitted) {
            return;
        }

        setAnswers(previous => ({
            ...previous,
            [question.id]: value
        }));

    };

    //----------------------------------------------------
    // Validate
    //----------------------------------------------------

    const hasAnswer = () => {

        if (question.type === "slider" ||
            question.type === "sequence") {
            return true;
        }

        if (Array.isArray(answer)) {
            return answer.length > 0;
        }

        if (question.type === "text") {
            return Boolean(answer?.trim());
        }

        return answer !== undefined &&
               answer !== null &&
               answer !== "";

    };

    //----------------------------------------------------
    // Submit
    //----------------------------------------------------

    const submitAnswer = () => {

        if (!hasAnswer()) {
            return;
        }

        setSubmitted(true);
        setStatus("Answer reviewed");

    };

	//----------------------------------------------------
	// Next Question / Complete Assessment
	//----------------------------------------------------

	const nextQuestion = () => {

	    if (questionIndex + 1 >= QUESTIONS.length) {

	        setPhase("complete");
	        setStatus("Assessment complete");
	        setShowReport(true);

	        const finalResults = QUESTIONS.map(item => ({
	            question: item,
	            correct: answersMatch(
	                item,
	                answers[item.id]
	            )
	        }));

	        const scoredResults = finalResults.filter(
	            item => item.correct !== null
	        );

	        const matchedResults = scoredResults.filter(
	            item => item.correct === true
	        );

	        speak(
	            "Congratulations. You have completed all ten " +
	            "questions in your interactive workplace assessment. " +
	            "You matched " +
	            matchedResults.length +
	            " of the " +
	            scoredResults.length +
	            " reference answers. " +
	            "Your assessment completion report is now available, " +
	            "including your responses, visual hazard selections " +
	            "and supporting evidence. " +
	            "You can also print the report or save it as a PDF."
	        );

	        return;
	    }

	    const nextIndex = questionIndex + 1;

	    setQuestionIndex(nextIndex);
	    setSubmitted(false);

	    setStatus(
	        `Question ${nextIndex + 1} of ${QUESTIONS.length}`
	    );

	    speak(QUESTIONS[nextIndex].narration);

	};

    //----------------------------------------------------
    // Restart
    //----------------------------------------------------

    const restart = () => {

        runIdRef.current += 1;
        clearTimer();

        setQuestionIndex(0);
        setAnswers({});
        setSubmitted(false);
        setPhase("intro");
        setSpeaking(false);
        setStatus("Fiona is ready");

    };

    //----------------------------------------------------
    // Close
    //----------------------------------------------------

    const close = () => {

        runIdRef.current += 1;
        clearTimer();

        if (onClose) {
            onClose();
        }

    };

    //----------------------------------------------------
    // Results
    //----------------------------------------------------

    const results = QUESTIONS.map(item => {

        const value = answers[item.id];

        return {
            question: item,
            answer: value,
            correct: answersMatch(item, value)
        };

    });

    const scored = results.filter(
        item => item.correct !== null
    );

    const correctCount = scored.filter(
        item => item.correct === true
    ).length;

    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <div className="eva-q-root">

            {/*===============================================
              Fiona Drawer
            ===============================================*/}

            <aside className="eva-q-drawer">

                <header className="eva-q-header">

                    <div>

                        <small>OMNISCAN PRO / EVA</small>

                        <strong>INTERACTIVE ASSESSMENT</strong>

                    </div>

                    <button
                        type="button"
                        onClick={close}
                        aria-label="Close questionnaire"
                    >
                        ×
                    </button>

                </header>

                <div className="eva-q-drawer-content">

                    {/*=======================================
                      Fiona
                    =======================================*/}

                    <div className="eva-q-fiona">

                        <EvaVideo
                            ref={videoRef}
                            visible={true}
                            onReady={handleReady}
                        />

                    </div>

                    <div className="eva-q-fiona-name">

                        <strong>FIONA</strong>

                        <span>
                            Virtual Assessment Guide
                        </span>

                    </div>

                    {/*=======================================
                      Introduction
                    =======================================*/}

                    {phase === "intro" && (

                        <section className="eva-q-section">

                            <div className="eva-q-eyebrow">
                                INTERACTIVE WORKPLACE TRAINING
                            </div>

                            <h2>
                                Safety Assessment
                            </h2>

                            <p>
                                Welcome to Eva's interactive
                                workplace assessment.
                            </p>

                            <p>
                                Fiona will guide you through ten
                                different question types, including
                                image recognition, hazard marking,
                                floor plans and photographic evidence.
                            </p>

                            <div className="eva-q-info">

                                <strong>10 interactive questions</strong>

                                <span>
                                    Guided by Fiona, with feedback
                                    and a completion summary.
                                </span>

                            </div>

                            <div className="eva-q-notice">

                                Demonstration assessment only.
                                Images and reference answers are
                                illustrative and are not a substitute
                                for a site-specific inspection.

                            </div>

                        </section>

                    )}

                    {/*=======================================
                      Active Question
                    =======================================*/}

                    {phase === "question" && (

                        <section className="eva-q-section">

                            <div className="eva-q-eyebrow">
                                ASSESSMENT IN PROGRESS
                            </div>

                            <div className="eva-q-progress-label">

                                <strong>
                                    Question {questionIndex + 1}
                                    {" "}of{" "}
                                    {QUESTIONS.length}
                                </strong>

                                <span>
                                    {progress}% complete
                                </span>

                            </div>

                            <div className="eva-q-progress">

                                <div
                                    style={{
                                        width: `${progress}%`
                                    }}
                                />

                            </div>

                            <h2>{question.title}</h2>

                            <p>{question.question}</p>

                            <div className="eva-q-speaking-status">

                                <span
                                    className={
                                        "eva-q-status-dot " +
                                        (speaking ? "active" : "")
                                    }
                                />

                                {speaking
                                    ? "Fiona is presenting the question"
                                    : submitted
                                        ? "Answer reviewed"
                                        : "Your response is required"}

                            </div>

                            <div className="eva-q-info">

                                <strong>
                                    Question type
                                </strong>

                                <span>
                                    {question.type.toUpperCase()}
                                </span>

                            </div>

                        </section>

                    )}

                    {/*=======================================
                      Completion
                    =======================================*/}

                    {phase === "complete" && (

                        <section className="eva-q-section">

                            <div className="eva-q-complete-symbol">
                                ✓
                            </div>

                            <div className="eva-q-eyebrow">
                                ASSESSMENT COMPLETE
                            </div>

                            <h2>Well done!</h2>

                            <p>
                                You've completed all ten
                                interactive questions.
                            </p>

                            <div className="eva-q-info">

                                <strong>
                                    {correctCount} of {scored.length}
                                    {" "}reference answers matched
                                </strong>

                                <span>
                                    The remaining questions captured
                                    observations or evidence.
                                </span>

                            </div>

                            <div className="eva-q-notice">

                                This is a demonstration result,
                                not a certified training or
                                compliance assessment.

                            </div>

                        </section>

                    )}

                </div>

                {/*===========================================
                  Drawer Footer
                ===========================================*/}

                <footer className="eva-q-footer">

                    <div className="eva-q-footer-status">
                        {status}
                    </div>

                    {phase === "intro" && (

                        <button
                            type="button"
                            className="eva-q-primary"
                            disabled={!ready}
                            onClick={startQuestionnaire}
                        >

                            {ready
                                ? "Start Assessment →"
                                : "Connecting to Fiona..."}

                        </button>

                    )}

                    {phase === "complete" && (

                        <>
						<button
						    type="button"
						    className="eva-q-primary"
						    onClick={() => setShowReport(true)}
						>
						    View Assessment Report →
						</button>
                            <button
                                type="button"
                                className="eva-q-primary"
                                onClick={close}
                            >
                                Finish Assessment
                            </button>

                            <button
                                type="button"
                                className="eva-q-secondary"
                                onClick={restart}
                            >
                                Restart
                            </button>

                        </>

                    )}

                </footer>

            </aside>

            {/*===============================================
              Interactive Question Popup
            ===============================================*/}

            {phase === "question" && (

                <div
                    className="eva-q-popup"
                    role="dialog"
                    aria-label={question.title}
                >

                    <div className="eva-q-popup-header">

                        <div>

                            <small>
                                QUESTION{" "}
                                {String(questionIndex + 1).padStart(2, "0")}
                                {" / "}
                                {String(QUESTIONS.length).padStart(2, "0")}
                            </small>

                            <strong>{question.title}</strong>

                        </div>

                    </div>

                    <div className="eva-q-popup-body">

                        <p className="eva-q-popup-question">
                            {question.question}
                        </p>

                        <QuestionInput
                            question={question}
                            answer={answer}
                            onChange={updateAnswer}
                            locked={submitted}
                        />

                        {submitted && (

                            <div
                                className={
                                    "eva-q-feedback " +
                                    (result === true
                                        ? "correct"
                                        : result === false
                                            ? "incorrect"
                                            : "neutral")
                                }
                            >

                                <strong>

                                    {result === true
                                        ? "✓ Reference answer matched"
                                        : result === false
                                            ? "Review the reference answer"
                                            : "✓ Response recorded"}

                                </strong>

                                <span>
                                    {question.explanation}
                                </span>

                            </div>

                        )}

                    </div>

                    <div className="eva-q-popup-footer">

                        <span>
                            {questionIndex + 1} / {QUESTIONS.length}
                        </span>

                        {!submitted ? (

                            <button
                                type="button"
                                disabled={!hasAnswer()}
                                onClick={submitAnswer}
                            >
                                Check Answer →
                            </button>

                        ) : (

                            <button
                                type="button"
                                onClick={nextQuestion}
                            >

                                {questionIndex === QUESTIONS.length - 1
                                    ? "View Results →"
                                    : "Next Question →"}

                            </button>

                        )}

                    </div>

                </div>

            )}
			{/*===============================================
			  Assessment Completion Report
			===============================================*/}

			{phase === "complete" && showReport && (

			    <EvaAssessmentReport
			        questions={QUESTIONS}
			        answers={answers}
			        onClose={() => setShowReport(false)}
			    />

			)}
        </div>

    );

}
